import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { socketService } from '../lib/socketService';
import { sendToTerminal } from '../lib/terminalLogger';

const LOG_PREFIX = '[WEBSOCKET_CONTEXT]';

interface WebSocketContextType {
  isConnected: boolean;
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastError: Error | null;
  reconnect: () => void;
  /** Habilita a conexão WebSocket (chamado após autenticação) */
  enable: () => void;
  /** Indica se a conexão foi habilitada */
  isEnabled: boolean;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

/**
 * Provider centralizado para gerenciar estado de conexão WebSocket
 *
 * Benefícios:
 * - Estado de conexão compartilhado entre todos os hooks
 * - Evita múltiplos listeners para eventos de conexão
 * - Centraliza lógica de reconexão
 * - Reduz re-renders desnecessários
 */
export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [lastError, setLastError] = useState<Error | null>(null);
  // ✅ OTIMIZAÇÃO: Conexão lazy - só conecta após autenticação
  const [isEnabled, setIsEnabled] = useState(false);

  // Função para habilitar conexão (chamada após login)
  const enable = useCallback(() => {
    if (!isEnabled) {
      sendToTerminal(`🔌 ${LOG_PREFIX} Habilitando conexão WebSocket (lazy)`, {
        source: 'app-enable',
      });
      setIsEnabled(true);
    }
  }, [isEnabled]);

  useEffect(() => {
    // ✅ OTIMIZAÇÃO: Não conectar se não habilitado
    if (!isEnabled) {
      return;
    }

    const socket = socketService.getSocket({
      source: 'app-enable',
      reason: 'websocket-context-enable',
    });
    if (!socket) {
      const configError = socketService.getConfigurationError();
      if (configError) {
        sendToTerminal(`❌ ${LOG_PREFIX} WebSocket indisponível por configuração`, {
          error: configError,
        });
        setIsConnected(false);
        setConnectionState('error');
        setLastError(new Error(configError));
      }
      return;
    }

    // Verificar estado inicial
    const checkInitialConnection = () => {
      const connected = socketService.isConnected();
      setIsConnected(connected);
      setConnectionState(connected ? 'connected' : 'disconnected');

      if (connected) {
        sendToTerminal(`✅ ${LOG_PREFIX} WebSocket já conectado`);
      }
    };

    checkInitialConnection();

    // Handler de conexão
    const handleConnect = () => {
      sendToTerminal(`✅ ${LOG_PREFIX} WebSocket conectado`);
      setIsConnected(true);
      setConnectionState('connected');
      setLastError(null);
    };

    // Handler de desconexão
    const handleDisconnect = (reason: string) => {
      sendToTerminal(`⚠️ ${LOG_PREFIX} WebSocket desconectado`, { reason });
      setIsConnected(false);
      setConnectionState('disconnected');
    };

    // Handler de erro
    const handleConnectError = (error: Error) => {
      const message = error?.message || '';
      const isAuthError = /WEBSOCKET_UNAUTHORIZED|WS_FORBIDDEN_USER_MISMATCH|unauthorized|401/i.test(message);

      sendToTerminal(`❌ ${LOG_PREFIX} Erro de conexão WebSocket`, {
        error: message,
        isAuthError
      });
      setIsConnected(false);
      setConnectionState('error');
      setLastError(error);

      // ✅ Em erro de auth: NÃO destruir o socket. O socketService já marca
      // `authFailed = true` internamente e bloqueia retries automáticos.
      // O próximo evento `auth:updated` (login/refresh) chama
      // `refreshAuthAndReconnect`, que reaplica token e reconecta sem precisar
      // recriar a instância. Destruir aqui causava o sintoma de "socket morto"
      // mesmo após refresh do token bem-sucedido.
      if (isAuthError) {
        sendToTerminal(`⏸️ ${LOG_PREFIX} Auth error — pausando socket até auth:updated`);
      }
    };

    // Handler de tentativa de reconexão
    const handleReconnectAttempt = (attempt: number) => {
      sendToTerminal(`🔄 ${LOG_PREFIX} Tentando reconectar...`, { attempt });
      setConnectionState('connecting');
    };

    // Registrar listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.io.on('reconnect_attempt', handleReconnectAttempt);

    // Cleanup
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.io.off('reconnect_attempt', handleReconnectAttempt);
    };
  }, [isEnabled]); // ✅ Reconectar quando habilitado

  // Atualizar handshake/auth do socket sempre que o token for alterado.
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const handleAuthUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ accessToken?: string | null }>).detail;
      if (!detail || !('accessToken' in detail)) {
        return;
      }
      sendToTerminal(`🔄 ${LOG_PREFIX} auth:updated recebido - renovando conexão WebSocket`);
      setConnectionState('connecting');
      setLastError(null);
      socketService.refreshAuthAndReconnect('auth:updated', {
        source: 'auth-updated',
        reason: 'websocket-context-auth-updated',
      });
    };

    window.addEventListener('auth:updated', handleAuthUpdated as EventListener);
    return () => {
      window.removeEventListener('auth:updated', handleAuthUpdated as EventListener);
    };
  }, [isEnabled]);

  const reconnect = useCallback(() => {
    if (!isEnabled) {
      sendToTerminal(`⚠️ ${LOG_PREFIX} Reconexão ignorada - WebSocket não habilitado`);
      return;
    }
    sendToTerminal(`🔄 ${LOG_PREFIX} Forçando reconexão...`);
    setConnectionState('connecting');
    setLastError(null);
    socketService.refreshAuthAndReconnect('manual_reconnect', {
      source: 'manual-reconnect',
      reason: 'websocket-context-manual-reconnect',
    });
  }, [isEnabled]);

  const value = useMemo<WebSocketContextType>(() => ({
    isConnected,
    connectionState,
    lastError,
    reconnect,
    enable,
    isEnabled
  }), [isConnected, connectionState, lastError, reconnect, enable, isEnabled]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook para acessar estado de conexão WebSocket
 *
 * @example
 * const { isConnected, connectionState } = useWebSocketConnection();
 */
export function useWebSocketConnection() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocketConnection must be used within WebSocketProvider');
  }
  return context;
}
